import {useMemo} from "react";
import {
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	PolarRadiusAxis,
	Radar,
	ResponsiveContainer,
	Tooltip,
} from "recharts";

import {
	computeRadarAreas,
	computeAllOveralls,
	computePlaystyles,
	ROLE_LABELS,
} from "@/config/smartValuationConfig";

import "./index.scss";
import {COLORS} from "@/style/utils";

// ── Component ─────────────────────────────────────────────────────────────────

export default function SmartRadarChart({values, playerName, compact = false}) {
	const radarData = useMemo(() => computeRadarAreas(values), [values]);
	const overalls = useMemo(() => computeAllOveralls(values), [values]);
	const playstyles = useMemo(() => computePlaystyles(values), [values]);

	const hasData = radarData.some((d) => d.value > 0);
	if (!hasData) return null;

	const bestRole = Object.entries(overalls).sort((a, b) => b[1] - a[1])[0];
	const topPlaystyles = playstyles.slice(0, 3);

	return (
		<div className={`src ${compact ? "src--compact" : ""}`}>
			{/* ── Radar ───────────────────────────────────────── */}
			<div className="src__chart">
				<ResponsiveContainer width="100%" height={compact ? 220 : 280}>
					<RadarChart data={radarData} cx="50%" cy="50%" outerRadius={compact ? 80 : 100}>
						<PolarGrid stroke={COLORS.border} />
						<PolarAngleAxis
							dataKey="area"
							tick={{fontSize: 11, fontWeight: 600, fill: COLORS.textSecondary}}
						/>
						<PolarRadiusAxis
							angle={90}
							domain={[0, 100]}
							tick={{fontSize: 9, fill: COLORS.textMuted}}
							tickCount={5}
						/>
						<Radar
							name={playerName || "Giocatore"}
							dataKey="value"
							stroke={COLORS.primary}
							fill={COLORS.primary}
							fillOpacity={0.25}
							strokeWidth={2}
							dot={{r: 4, fill: COLORS.primary}}
						/>
						<Tooltip
							contentStyle={{fontSize: 12, borderRadius: 8, border: "1px solid " + COLORS.border}}
							formatter={(v) => [`${v}/100`, ""]}
						/>
					</RadarChart>
				</ResponsiveContainer>
			</div>

			{/* ── Overall per ruolo ────────────────────────────── */}
			<div className="src__overalls">
				{Object.entries(overalls).map(([role, score]) => (
					<div
						key={role}
						className={`src__overall ${bestRole[0] === role ? "src__overall--best" : ""}`}
					>
						<span className="src__overall-score">{score}</span>
						<span className="src__overall-role">{ROLE_LABELS[role]}</span>
					</div>
				))}
			</div>

			{/* ── Playstyles ───────────────────────────────────── */}
			{topPlaystyles.length > 0 && (
				<div className="src__playstyles">
					<div className="src__playstyles-title">Playstyle</div>
					<div className="src__playstyles-list">
						{topPlaystyles.map((ps, i) => (
							<div key={ps.id} className={`src__playstyle ${i === 0 ? "src__playstyle--top" : ""}`}>
								<span className="src__playstyle-emoji">{ps.emoji}</span>
								<span className="src__playstyle-label">{ps.label}</span>
								<span className="src__playstyle-score">{ps.score}</span>
								<div className="src__playstyle-bar">
									<div
										className="src__playstyle-fill"
										style={{width: `${ps.score}%`}}
									/>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
